import React from 'react';
import { IHTCalculation, EstateAssets, EstateProfile } from '../../types';
import { resolveResidenceForConsumers } from '../../server/services/rnrbResidenceFacts';
import { SectionHeader } from '../ui';

type Severity = 'info' | 'caution' | 'specialist_review';

interface RiskAlert {
    id: string;
    title: string;
    explanation: string;
    severity: Severity;
    suggestions?: string[];
}

const fmt = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

function detectRisks(
    ihtCalc: IHTCalculation | null | undefined,
    assets: EstateAssets | undefined,
    profile: EstateProfile | undefined,
): RiskAlert[] {
    const alerts: RiskAlert[] = [];
    const b = ihtCalc?.breakdown;

    // Large gifts close to death
    if ((b?.gifts_chargeable_total ?? 0) > 50000) {
        alerts.push({
            id: 'large_gifts',
            title: 'Significant lifetime gifts',
            explanation: 'Large chargeable gifts have been recorded. These reduce the Nil Rate Band and may attract additional IHT if made within 7 years of death.',
            severity: 'caution',
        });
    }

    // Estate near or above £2m (RNRB taper zone)
    const netValue = b?.net_value ?? b?.gross_value ?? 0;
    if (netValue > 1800000 && netValue <= 2000000) {
        alerts.push({
            id: 'near_taper',
            title: 'Estate approaching RNRB taper threshold',
            explanation: 'The estate is close to £2 million. Above this threshold, the Residence Nil Rate Band is reduced by £1 for every £2 over £2m.',
            severity: 'info',
        });
    }
    // The taper reduces the allowance; RNRB is the lower of that adjusted allowance and the
    // residence value (PA-IHT-RNRB-TAPER-CALC-001). Only alert when RNRB itself was reduced.
    const rnrbAvailable = b?.rnrb_available ?? 0;
    const rnrbBefore = b?.rnrb_before_taper ?? 0;
    if (netValue > 2000000 && (b?.taper_amount ?? 0) > 0 && rnrbAvailable < rnrbBefore) {
        alerts.push({
            id: 'rnrb_taper',
            title: rnrbAvailable === 0 ? 'RNRB fully tapered away' : 'RNRB partially tapered',
            explanation: rnrbAvailable === 0
                ? 'No Residence Nil Rate Band is available because the estate exceeds the taper threshold. The full RNRB entitlement has been removed.'
                : 'Part of the Residence Nil Rate Band has been lost because the estate exceeds the £2 million taper threshold.',
            severity: 'caution',
        });
    }

    // Trust involvement
    const ts = profile?.iht_details?.trust_screening;
    if (ts?.trust_involvement_detected === true) {
        alerts.push({
            id: 'trust_involvement',
            title: 'Trust involvement detected',
            explanation: 'Assets held in trust may form part of the estate for IHT purposes. The type of trust determines the tax treatment.',
            severity: ts.specialist_review_needed ? 'specialist_review' : 'caution',
        });
    } else if (ts?.trust_involvement_uncertain === true) {
        alerts.push({
            id: 'trust_uncertain',
            title: 'Possible trust involvement (unconfirmed)',
            explanation: 'You indicated uncertainty about whether trusts or reserved-benefit gifts were involved. Consider reviewing financial records or seeking professional advice to confirm.',
            severity: 'info',
        });
    }

    // Gift with reservation
    if (ts?.gwr_likely === true) {
        alerts.push({
            id: 'gwr_risk',
            title: 'Likely gift with reservation',
            explanation: 'Assets were given away but the donor continued to benefit without paying full market consideration. These assets may still form part of the estate for IHT purposes (IHTA84 s102).',
            severity: 'specialist_review',
        });
    } else if (ts?.gwr_possible === true) {
        alerts.push({
            id: 'gwr_risk',
            title: 'Possible gift with reservation',
            explanation: 'Assets given away where the donor retained a benefit may still be treated as part of the estate for IHT purposes (IHTA84 s102). Provide more details to clarify.',
            severity: 'caution',
        });
    }

    // Foreign assets — cross-border complexity
    if (assets?.foreign_assets?.exists === true) {
        const countries = assets.foreign_assets.countries ?? [];
        const assetTypes = assets.foreign_assets.asset_types ?? [];
        const countriesWithTax = countries.filter(c => ['France', 'Spain', 'Italy', 'Portugal', 'USA'].includes(c));
        const countryNote = countriesWithTax.length > 0
            ? ` ${countriesWithTax.join(', ')} ${countriesWithTax.length === 1 ? 'has its' : 'have their'} own inheritance tax rules.`
            : '';

        // Type-specific guidance
        const typeNotes: string[] = [];
        if (assetTypes.includes('property')) {
            typeNotes.push('Foreign property may require separate probate or legal procedures in the relevant country.');
        }
        if (assetTypes.includes('bank_accounts') || assetTypes.includes('investments')) {
            typeNotes.push('Foreign financial assets may require additional documentation or local procedures to release.');
        }
        if (assetTypes.includes('business')) {
            typeNotes.push('Foreign business interests should be reviewed by a specialist to determine correct IHT treatment.');
        }
        const typeGuidance = typeNotes.length > 0 ? ' ' + typeNotes.join(' ') : '';

        alerts.push({
            id: 'foreign_assets',
            title: 'Foreign assets require cross-border probate',
            explanation: `The estate includes assets outside the UK.${countryNote}${typeGuidance}`,
            severity: assetTypes.includes('business') || (assets.foreign_assets.value_estimate ?? 0) > 250000 ? 'caution' : 'info',
        });
    }

    // Missing property valuations
    if (assets?.properties.some(p => !p.estimated_value || p.estimated_value === 0)) {
        alerts.push({
            id: 'missing_property_val',
            title: 'Missing property valuation',
            explanation: 'At least one property does not have a valuation entered. HMRC requires date-of-death valuations for all property assets.',
            severity: 'caution',
        });
    }

    // Missing spouse-transfer records
    const sp = profile?.iht_details?.predeceased_spouse;
    if (sp?.exists === true && sp?.records_available === 'none') {
        alerts.push({
            id: 'missing_spouse_records',
            title: 'Missing spouse-transfer records',
            explanation: 'No records are available from the first spouse\'s estate. This may affect the ability to claim transferable NRB or RNRB. Consider requesting probate records.',
            severity: 'caution',
        });
    }

    // Unclear ownership of major assets
    const unknownOwnership = [
        ...(assets?.properties ?? []).filter(p => !p.ownership_type || p.ownership_type === 'unknown'),
        ...(assets?.bank_accounts ?? []).filter(a => !a.ownership),
        ...(assets?.investments ?? []).filter(i => !i.ownership),
    ];
    if (unknownOwnership.length > 0) {
        alerts.push({
            id: 'unclear_ownership',
            title: 'Unclear ownership on some assets',
            explanation: `${unknownOwnership.length} asset${unknownOwnership.length > 1 ? 's have' : ' has'} unknown ownership type. This affects probate and IHT calculations.`,
            severity: 'info',
        });
    }

    // TRNRB uncertainty — the engine's TRNRB claim flag ('missing': claim unanswered/unsure, or claimed
    // with no percentage). predeceased_spouse.trnrb_confidence is never stored, so it could not fire (D06).
    if (sp?.exists === true && (ihtCalc?.confidence?.flags ?? []).some(f => f.field === 'trnrb_claim' && f.level === 'missing')) {
        alerts.push({
            id: 'trnrb_uncertain',
            title: 'Transferable RNRB unconfirmed',
            explanation: 'A transferable Residence Nil Rate Band may be available but has not been confirmed. This could reduce the IHT estimate if verified.',
            severity: 'info',
        });
    }

    // Gift taper relief applied
    if ((b?.taper_relief_total ?? 0) > 0) {
        alerts.push({
            id: 'taper_relief',
            title: 'Gift taper relief reduces the tax bill',
            explanation: `Taper relief of ${fmt(b!.taper_relief_total!)} has been applied because some chargeable gifts were made more than 3 years before death. The tax rate on those gifts is reduced.`,
            severity: 'info',
        });
    }

    // Possible income gift exemption (Part 2)
    if (assets?.possible_income_gift_exemption === true || assets?.possible_income_gift_exemption === null) {
        // Only show if gifts exist and flag is set
        if (assets?.lifetime_gifts && assets.lifetime_gifts.length > 0 && assets?.possible_income_gift_exemption !== undefined) {
            alerts.push({
                id: 'income_gift_exemption',
                title: 'Possible "normal expenditure out of income" exemption',
                explanation: 'Some gifts may qualify for the normal expenditure out of income exemption (IHTA84 s21) and may not count towards inheritance tax. This requires evidence that gifts were regular, made from surplus income, and did not reduce the donor\'s standard of living.',
                severity: 'info',
            });
        }
    }

    // Life insurance (Part 3)
    const lifeIns = assets?.life_insurance;
    if (lifeIns?.exists === true) {
        if (lifeIns.written_in_trust === true) {
            alerts.push({
                id: 'life_insurance_trust',
                title: 'Life insurance written in trust',
                explanation: 'The life insurance payout is excluded from the estate because the policies were written in trust. The proceeds go directly to the trust beneficiaries.',
                severity: 'info',
            });
        } else if (lifeIns.written_in_trust === false) {
            alerts.push({
                id: 'life_insurance_estate',
                title: 'Life insurance included in estate',
                explanation: 'Life insurance policies not written in trust form part of the estate for IHT purposes. The payout value has been included in the estate total.',
                severity: 'info',
            });
        } else {
            alerts.push({
                id: 'life_insurance_uncertain',
                title: 'Life insurance trust status unknown',
                explanation: 'It is unclear whether life insurance policies were written in trust. If they were, the payout would be excluded from the estate. Check policy documents or contact the insurer to confirm.',
                severity: 'caution',
            });
        }
    }

    // Joint property messaging (Part 4). The home is the QRI from the shared Stage-1 resolution (never
    // is_main_residence); with none resolved nothing is said, rather than treating an unknown as "no home".
    const residence = resolveResidenceForConsumers(assets?.properties, profile?.iht_details);
    const mainHome = residence.qri;
    const provisionalNote = residence.provisional ? ' (This property is treated as the home provisionally: it is not yet confirmed that they lived in it.)' : '';
    if (mainHome) {
        if (mainHome.ownership_type === 'joint_tenants') {
            alerts.push({
                id: 'joint_tenancy_home',
                title: 'Main home held as joint tenants',
                explanation: 'The main home passes automatically to the surviving joint owner by right of survivorship. Probate is not needed for this asset, but the deceased\'s share is still included in the estate for IHT purposes.' + provisionalNote,
                severity: 'info',
            });
        } else if (mainHome.ownership_type === 'tenants_in_common') {
            alerts.push({
                id: 'tenants_in_common_home',
                title: 'Main home held as tenants in common',
                explanation: 'The deceased\'s share of the property does not pass automatically to the co-owner. Probate will likely be needed to transfer the deceased\'s share according to the Will or intestacy rules.' + provisionalNote,
                severity: 'info',
            });
        }
    }

    // Estate liquidity warning (Part 5) — proper liquid assets check
    const taxDue = ihtCalc?.total_tax_due ?? 0;
    const propertyTotal = assets?.properties?.reduce((sum, p) => {
        const val = Number(p.estimated_value) || 0;
        const share = (p.deceased_share_percent ?? 100) / 100;
        return sum + (val * share);
    }, 0) ?? 0;

    if (taxDue > 0) {
        // Calculate liquid assets (bank accounts + cash-like)
        const liquidAssets = (assets?.bank_accounts ?? []).reduce((sum, a) => {
            const val = Number(a.estimated_value) || 0;
            return sum + (a.ownership === 'joint' ? val / 2 : val);
        }, 0);

        if (liquidAssets < taxDue) {
            const shortfall = taxDue - liquidAssets;
            alerts.push({
                id: 'estate_liquidity',
                title: 'Potential estate liquidity issue detected',
                explanation: `Liquid assets (bank accounts: ${fmt(liquidAssets)}) may not cover the estimated inheritance tax liability of ${fmt(taxDue)}. ` +
                    `Shortfall: approximately ${fmt(shortfall)}.`,
                severity: 'caution',
                suggestions: [
                    'HMRC instalment option (for qualifying assets like property)',
                    'Executor loan or bridging finance',
                    'Property sale to raise funds',
                    'Direct payment arrangement with HMRC',
                ],
            });
        }

        // Property instalment estimation
        const grossValue = b?.gross_value ?? 0;
        if (grossValue > 0 && propertyTotal / grossValue > 0.50) {
            const annualInstalment = Math.round(taxDue / 10);
            alerts.push({
                id: 'property_instalment',
                title: 'Property-related inheritance tax may be paid in instalments',
                explanation: `Property makes up more than 50% of the estate value. HMRC may allow the tax to be paid in 10 equal annual instalments (plus interest on outstanding balance). ` +
                    `Estimated annual instalment: ${fmt(annualInstalment)} (+ interest).`,
                severity: 'info',
            });
        }
    }

    return alerts;
}

const SEVERITY_LABEL: Record<Severity, string> = {
    info: 'Note',
    caution: 'Attention',
    specialist_review: 'Specialist review',
};

interface Props {
    ihtCalc?: IHTCalculation | null;
    assets?: EstateAssets;
    profile?: EstateProfile;
}

export const ExecutorRiskAlerts: React.FC<Props> = ({ ihtCalc, assets, profile }) => {
    const alerts = detectRisks(ihtCalc, assets, profile);
    if (alerts.length === 0) return null;

    return (
        <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
            <div className="px-6 pt-6">
                <SectionHeader
                    numeral=""
                    title="Possible issues detected"
                    subtitle={`${alerts.length} item${alerts.length !== 1 ? 's' : ''} may require attention.`}
                />
            </div>
            <div className="px-6 pb-6">
                {alerts.map(alert => (
                    <div key={alert.id} className="py-3 border-b border-dotted border-[#E8E4DB] last:border-0">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <p className="font-serif text-[15px] font-medium text-[#1A1A1A] leading-snug">{alert.title}</p>
                                <p className="text-[13px] text-[#3F3D39] mt-1 leading-relaxed">{alert.explanation}</p>
                                {alert.suggestions && alert.suggestions.length > 0 && (
                                    <div className="mt-2">
                                        <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-1">Executors may need to consider</p>
                                        <ul className="space-y-0.5">
                                            {alert.suggestions.map((s, i) => (
                                                <li key={i} className="text-[12px] text-[#3F3D39] flex items-start gap-1.5">
                                                    <span className="text-[#6B6762]">·</span>
                                                    <span>{s}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                            <span className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] whitespace-nowrap shrink-0 mt-1">
                                {SEVERITY_LABEL[alert.severity]}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
